import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from '../config';
import formatUrl from '../utils/urlFormatter';
import Notification from '../components/Notification';
import SearchSuggestions from '../components/SearchSuggestions';
import { useAdvancedSearch } from '../hooks/useAdvancedSearch';
import SearchService from '../services/SearchService';
import '../styles/discoverUsers.css';

const DiscoverUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filters, setFilters] = useState({ city: '', sortBy: 'popular' });
    const [cities, setCities] = useState([]);
    const [error, setError] = useState(null);
    const [nextPage, setNextPage] = useState(null);
    const [notification, setNotification] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const filtersRef = useRef(filters);
    const navigate = useNavigate();
    filtersRef.current = filters;

    // Use search history from the hook
    const {
        isLoading: searchLoading,
        getSearchHistory,
        clearSearchHistory
    } = useAdvancedSearch('', 'users');

    // Get search history
    const searchHistory = getSearchHistory ? getSearchHistory() : [];
    const clearHistory = clearSearchHistory || (() => {});

    // Users are now fetched pre-sorted/filtered from backend
    const displayedUsers = users;

    // Fetch all users and cities on initial load
    useEffect(() => {
        fetchUsers();
        fetchCities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when filters change
    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.city, filters.sortBy]);

    // Trigger backend search when searchTerm changes (debounced)
    useEffect(() => {
        // Skip if this is initial mount (searchTerm is empty from useState)
        if (searchTerm === '') return;

        const debounceTimer = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                fetchUsers(null, searchTerm.trim());
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // Generate intelligent user suggestions
    const generateUserSuggestions = (query, userList) => {
        const searchQuery = query.toLowerCase();
        const suggestions = [];
        
        userList.forEach(user => {
            const username = user?.username?.toLowerCase() || '';
            const firstName = user?.first_name?.toLowerCase() || '';
            const lastName = user?.last_name?.toLowerCase() || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const city = user?.city_name || '';
            
            if (username.includes(searchQuery)) {
                suggestions.push(`@${user.username}`);
            }
            if (fullName.includes(searchQuery)) {
                suggestions.push(fullName);
            }
            if (city.toLowerCase().includes(searchQuery)) {
                suggestions.push(city);
            }
        });
        
        return [...new Set(suggestions)].slice(0, 5); // Unique suggestions, max 5
    };

    const fetchUsers = useCallback(async (url = null, searchQuery = null) => {
        setLoading(true);
        setError(null);
        
        try {
            // Build API URL with search parameter like the app does
            let apiUrl = url || `${config.backendUrl}/users/discover/`;
            
            // Build query params: search, city, ordering
            const params = new URLSearchParams();
            if (searchQuery && searchQuery.trim()) {
                params.set('search', searchQuery.trim());
            }
            const currentFilters = filtersRef.current;
            if (currentFilters.city) {
                params.set('city', currentFilters.city);
            }
            const sortMap = {
                popular: '-follower_count',
                recent: '-date_joined',
                products: '-product_count',
                alphabetical: 'first_name',
            };
            if (sortMap[currentFilters.sortBy]) {
                params.set('ordering', sortMap[currentFilters.sortBy]);
            }
            const queryString = params.toString();
            if (queryString && !url) {
                const baseUrl = apiUrl.split('?')[0];
                apiUrl = `${baseUrl}?${queryString}`;
            }
            
            const token = Cookies.get('authToken');
            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.status}`);
            }
            
            const data = await response.json();
            const usersArray = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);

            // Normalize user data to match app structure
            const normalizedUsers = usersArray.map(user => ({
                ...user,
                total_products: user.total_products || user.product_count || 0,
                total_followers: user.total_followers || user.follower_count || 0,
                average_rating: user.average_rating || 0,
                product_count: user.total_products || user.product_count || 0,
                follower_count: user.total_followers || user.follower_count || 0,
                following_count: user.following_count || 0
            }));

            // Enhanced user data processing
            setUsers(prevUsers => {
                if (!url) {
                    // Initial load or new search - replace all users
                    return normalizedUsers;
                } else {
                    // Load more - append unique users
                    const existingIds = new Set(prevUsers.map(user => user.id));
                    const newUsers = normalizedUsers.filter(user => !existingIds.has(user.id));
                    return [...prevUsers, ...newUsers];
                }
            });

            setNextPage(data.next);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Unable to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCities = useCallback(async () => {
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/users/cities/`, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch cities');
            }
            
            const data = await response.json();
            setCities(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        }
    }, []);

    const handleLoadMore = async () => {
        if (!nextPage || loadingMore) return;
        
        setLoadingMore(true);
        try {
            await fetchUsers(nextPage);
        } catch (error) {
            console.error('Error loading more users:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleFollow = async (userId) => {
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/users/follow/${userId}/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to follow user');
            }
            
            // Refresh users to update follow status
            fetchUsers();
            
            // Show success notification
            setNotification({
                message: 'User followed successfully!',
                type: 'success',
                onClose: () => setNotification(null)
            });
        } catch (error) {
            console.error('Error following user:', error);
            setNotification({
                message: 'Failed to follow user. Please try again.',
                type: 'error',
                onClose: () => setNotification(null)
            });
        }
    };

    const handleUnfollow = async (userId) => {
        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/users/unfollow/${userId}/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to unfollow user');
            }
            
            // Refresh users to update follow status
            fetchUsers();
            
            // Show success notification
            setNotification({
                message: 'User unfollowed successfully!',
                type: 'success',
                onClose: () => setNotification(null)
            });
        } catch (error) {
            console.error('Error unfollowing user:', error);
            setNotification({
                message: 'Failed to unfollow user. Please try again.',
                type: 'error',
                onClose: () => setNotification(null)
            });
        }
    };

    const handleSuggestionSelect = (suggestion) => {
        setSearchTerm(suggestion);
        setShowSuggestions(false);
        
        // Add to search history
        SearchService.addToSearchHistory(suggestion);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setShowSuggestions(false);
        // Fetch all users again when search is cleared
        fetchUsers();
    };

    const getRandomColor = (seed) => {
        const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316'];
        return colors[seed % colors.length];
    };

    const getInitials = (firstName, lastName, username) => {
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`;
        } else if (username) {
            return username.charAt(0).toUpperCase();
        }
        return 'U';
    };

    // Enhanced search form submission with validation
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            SearchService.addToSearchHistory(searchTerm.trim());
            setShowSuggestions(false);
        }
    };

    return (
        <div className="discover-container">
            <div className="discover-header">
                <h1>Découvrir des utilisateurs</h1>
                <p>Trouvez et connectez-vous avec d'autres membres de la communauté</p>
            </div>

            <div className="search-filters-wrapper">
                <form className="search-form" onSubmit={handleSearchSubmit}>
                    <div className="search-input-wrapper">
                        <i className="search-icon">🔍</i>
                        <input
                            type="text"
                            placeholder="Rechercher par nom, nom d'utilisateur ou ville..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setShowSuggestions(true)}
                            className="search-input"
                            autoComplete="off"
                        />
                        
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                className="clear-search-btn"
                                aria-label="Clear search"
                            >
                                ✕
                            </button>
                        )}
                        
                        {searchLoading && (
                            <div className="search-loading-indicator">
                                <div className="spinner"></div>
                            </div>
                        )}
                    </div>

                    {/* Enhanced Search Suggestions */}
                    {showSuggestions && searchHistory.length > 0 && (
                        <SearchSuggestions
                            suggestions={[]}
                            searchHistory={searchHistory}
                            onSuggestionSelect={handleSuggestionSelect}
                            onClose={() => setShowSuggestions(false)}
                            clearHistory={clearHistory}
                        />
                    )}
                </form>

                <div className="filters-row">
                    <select
                        value={filters.city}
                        onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                        className={`filter-select${filters.city ? ' active' : ''}`}
                    >
                        <option value="">📍 Toutes les villes</option>
                        {cities.map(city => (
                            <option key={city.id} value={city.name}>
                                {city.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={filters.sortBy}
                        onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                        className="filter-select"
                    >
                        <option value="popular">⭐ Les plus populaires</option>
                        <option value="recent">🕒 Récemment actifs</option>
                        <option value="products">📦 Plus de produits</option>
                        <option value="alphabetical">🔤 Ordre alphabétique</option>
                    </select>

                    {(filters.city || searchTerm) && (
                        <button
                            type="button"
                            className="filter-reset-btn"
                            onClick={() => {
                                setFilters({ city: '', sortBy: 'popular' });
                                if (searchTerm) clearSearch();
                            }}
                        >
                            ✕ Réinitialiser
                        </button>
                    )}
                </div>
            </div>

            {/* Enhanced Error Display */}
            {error && (
                <div className="error-banner">
                    <span className="error-icon">⚠️</span>
                    <span>{error}</span>
                    <button 
                        onClick={() => {
                            setError(null);
                            fetchUsers();
                        }}
                        className="retry-btn"
                    >
                        Retry
                    </button>
                </div>
            )}

            <div className="users-grid enhanced">
                {loading ? (
                    // Enhanced loading skeletons
                    [...Array(8)].map((_, i) => (
                        <div key={i} className="user-card skeleton enhanced">
                            <div className="skeleton-header">
                                <div className="skeleton-avatar"></div>
                                <div className="skeleton-info">
                                    <div className="skeleton-line"></div>
                                    <div className="skeleton-line short"></div>
                                </div>
                            </div>
                            <div className="skeleton-stats">
                                <div className="skeleton-stat"></div>
                                <div className="skeleton-stat"></div>
                                <div className="skeleton-stat"></div>
                            </div>
                            <div className="skeleton-button"></div>
                        </div>
                    ))
                ) : displayedUsers.length > 0 ? (
                    displayedUsers.map(user => (
                        <div key={user.id} className="user-card enhanced">
                            <div className="user-card-header">
                                <div
                                    className="user-avatar"
                                    style={{
                                        backgroundColor: (user.profile_picture || user.profile_picture_url) ? 'transparent' : getRandomColor(user.id)
                                    }}
                                >
                                    {(user.profile_picture || user.profile_picture_url) ? (
                                        <img
                                            src={formatUrl(user.profile_picture || user.profile_picture_url)}
                                            alt={`${user.first_name} ${user.last_name}`}
                                            loading="lazy"
                                            onError={(e) => {
                                                // Hide broken image and show initials fallback
                                                e.target.style.display = 'none';
                                                const parent = e.target.parentElement;
                                                if (parent && !parent.querySelector('.initials')) {
                                                    const initialsEl = document.createElement('span');
                                                    initialsEl.className = 'initials';
                                                    initialsEl.textContent = getInitials(user.first_name, user.last_name, user.username);
                                                    parent.style.backgroundColor = getRandomColor(user.id);
                                                    parent.appendChild(initialsEl);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <span className="initials">
                                            {getInitials(user.first_name, user.last_name, user.username)}
                                        </span>
                                    )}
                                    {user.is_new && <span className="new-badge">Nouveau</span>}
                                </div>
                                
                                <div className="user-info">
                                    <h3 className="user-name">
                                        {user.first_name && user.last_name 
                                            ? `${user.first_name} ${user.last_name}`
                                            : user.username
                                        }
                                    </h3>
                                    <p className="user-username">@{user.username}</p>
                                    {user.city_name && (
                                        <p className="user-location">
                                            <span className="location-icon">📍</span>
                                            {user.city_name}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="user-stats">
                                <div className="stat">
                                    <span className="stat-number">{user.total_followers || user.follower_count || 0}</span>
                                    <span className="stat-label">Abonnés</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-number">{user.following_count || 0}</span>
                                    <span className="stat-label">Abonnements</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-number">{user.total_products || user.product_count || 0}</span>
                                    <span className="stat-label">Produits</span>
                                </div>
                            </div>

                            <div className="user-actions">
                                {user.is_following ? (
                                    <button
                                        className="follow-button following"
                                        onClick={() => handleUnfollow(user.uuid || user.id)}
                                    >
                                        Suivi ✓
                                    </button>
                                ) : (
                                    <button
                                        className="follow-button"
                                        onClick={() => handleFollow(user.uuid || user.id)}
                                    >
                                        + Suivre
                                    </button>
                                )}
                                
                                <button
                                    className="view-profile-button"
                                    onClick={() => navigate(`/user/corner/${user.uuid || user.id}/`)}  // Use UUID if available
                                >
                                    Voir la boutique
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-state enhanced">
                        <div className="empty-icon">👤</div>
                        <h3>Aucun utilisateur trouvé</h3>
                        <p>
                            {searchTerm.trim() 
                                ? `Aucun résultat pour "${searchTerm}". Essayez de modifier vos filtres ou votre recherche.`
                                : 'Modifiez vos filtres pour découvrir des utilisateurs.'
                            }
                        </p>
                        {searchTerm.trim() && (
                            <button onClick={clearSearch} className="clear-search-button">
                                Effacer la recherche
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Enhanced Load More */}
            {nextPage && !loading && (
                <div className="load-more-container enhanced">
                    <button
                        className="load-more-button"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? (
                            <>
                                <div className="spinner small"></div>
                                Chargement...
                            </>
                        ) : (
                            'Charger plus'
                        )}
                    </button>
                </div>
            )}

            {/* Enhanced Notification */}
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onConfirm={notification.onConfirm}
                    onClose={notification.onClose}
                />
            )}
        </div>
    );
};

export default DiscoverUsers;
